import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Slider,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@mui/material';
import {
  Close,
  Save,
  Business,
  TrendingUp,
  People,
  LocationOn,
  AccountBalance,
  CorporateFare,
  Language,
  CloudUpload
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext'; // Your auth context
import axios from 'axios';

// Enhanced Default Company Context with Parent/GCC separation
const DEFAULT_COMPANY_CONTEXT = {
  // Parent Company Information
  parentCompany: {
    name: "Global Tech Corp",
    headquarters: "United States",
    industry: "Technology",
    revenue: 10000000000, // $10B
    employeeCount: 50000,
    annualITBudget: 1000000000, // $1B
    operatingMargin: 0.18, // 18%
    revenueGrowthRate: 0.12, // 12%
    digitalMaturity: "Medium",
    primaryCurrency: "USD"
  },
  
  // GCC Company Information
  gccCompany: {
    name: "Global Tech Corp GCC India",
    location: "India",
    targetSize: 500,
    currentSize: 0,
    establishmentYear: new Date().getFullYear(),
    primaryServices: ["Software Development", "IT Operations", "Data Analytics"],
    budgetAllocation: "$50M over 5 years",
    strategicIntent: "Digital Transformation & Innovation Center",
    
    // GCC Specific Parameters
    locationMultiplier: 1.2,
    talentCostRatio: 0.6, // 60% of HQ cost
    expectedEfficiencyGain: 0.35, // 35%
    innovationContribution: 0.25, // 25%
    implementationTimeline: 36, // months
    maturityLevel: "Planned", // Planned, Establishing, Operational, Mature
    
    // Location Specific
    timeZone: "IST",
    language: "English",
    infrastructureRating: 4, // 1-5 scale
    talentAvailability: "High"
  },
  
  // Financial Parameters
  financialParameters: {
    discountRate: 0.10,
    taxRate: 0.25,
    costOfCapital: 0.08,
    workingCapitalRatio: 0.15,
    exchangeRate: 82.5, // Local currency to USD
    inflationRate: 0.05
  },
  
  // Current State Assessment
  currentState: {
    executiveSponsorship: "Medium",
    marketConditions: "Favorable",
    regulatoryEnvironment: "Stable",
    competitiveLandscape: "Moderate"
  },
  
  // Metadata
  metadata: {
    createdBy: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    version: "1.0",
    isActive: true
  }
};

const CompanyContextConfiguration = ({ 
  open = false, 
  onClose, 
  onSave,
  currentContext = DEFAULT_COMPANY_CONTEXT 
}) => {
  const { user } = useContext(AuthContext); // Get user from auth context
  const [context, setContext] = useState(JSON.parse(JSON.stringify(DEFAULT_COMPANY_CONTEXT)));
  const [activeSection, setActiveSection] = useState('parent');
  const [activeStep, setActiveStep] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Sync with currentContext when dialog opens
useEffect(() => {
  if (open) {
    // Use currentContext if provided, otherwise use DEFAULT_COMPANY_CONTEXT
    const initialContext = currentContext || DEFAULT_COMPANY_CONTEXT;
    const updatedContext = JSON.parse(JSON.stringify(initialContext));
    
    // Update metadata with current user
    if (user) {
      updatedContext.metadata.createdBy = user.id;
      updatedContext.metadata.updatedAt = new Date().toISOString();
    }
    setContext(updatedContext);
    setHasChanges(false);
    setSaveMessage('');
  }
}, [open, currentContext, user]);


  const handleContextChange = (section, field, value, subField = null) => {
    setContext(prev => {
      const newContext = JSON.parse(JSON.stringify(prev));
      if (subField) {
        newContext[section][subField][field] = value;
      } else if (section) {
        newContext[section][field] = value;
      } else {
        newContext[field] = value;
      }
      
      // Update metadata
      newContext.metadata.updatedAt = new Date().toISOString();
      if (user) {
        newContext.metadata.createdBy = user.id;
      }
      
      return newContext;
    });
    setHasChanges(true);
  };

  // Save to MongoDB
  const handleSave = async () => {
    if (!user) {
      setSaveMessage('Error: User not authenticated');
      return;
    }

    if (!user.is_subscribed) {
      setSaveMessage('Error: Subscription required to save company context');
      return;
    }

    setIsSubmitting(true);
    setSaveMessage('Saving...');

    try {
      const response = await axios.post('/api/gcc/company-context', {
        companyContext: context,
        userId: user.id,
        userEmail: user.email
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.data.success) {
        setSaveMessage('Successfully saved company context!');
        setHasChanges(false);
        
        if (onSave) {
          onSave(JSON.parse(JSON.stringify(context)));
        }
        
        // Close after successful save
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving company context:', error);
      setSaveMessage(`Error: ${error.response?.data?.message || 'Failed to save company context'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

const formatCurrency = (value) => {
  // Add null/undefined check
  if (value === undefined || value === null) {
    return '$0';
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const parseCurrency = (value) => {
  // Add null/undefined check
  if (!value) return 0;
  
  return parseInt(value.replace(/[^0-9.-]+/g, "")) || 0;
};


  const ParentCompanySection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        🏢 Parent Company Information
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure your global headquarters or parent company details.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Parent Company Name"
            value={context.parentCompany.name}
            onChange={(e) => handleContextChange('parentCompany', 'name', e.target.value)}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Headquarters Location</InputLabel>
            <Select
              value={context.parentCompany.headquarters}
              label="Headquarters Location"
              onChange={(e) => handleContextChange('parentCompany', 'headquarters', e.target.value)}
            >
              <MenuItem value="United States">United States</MenuItem>
              <MenuItem value="United Kingdom">United Kingdom</MenuItem>
              <MenuItem value="Germany">Germany</MenuItem>
              <MenuItem value="Japan">Japan</MenuItem>
              <MenuItem value="Australia">Australia</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Industry</InputLabel>
            <Select
              value={context.parentCompany.industry}
              label="Industry"
              onChange={(e) => handleContextChange('parentCompany', 'industry', e.target.value)}
            >
              <MenuItem value="Technology">Technology</MenuItem>
              <MenuItem value="Financial Services">Financial Services</MenuItem>
              <MenuItem value="Healthcare">Healthcare</MenuItem>
              <MenuItem value="Manufacturing">Manufacturing</MenuItem>
              <MenuItem value="Retail">Retail</MenuItem>
              <MenuItem value="Telecommunications">Telecommunications</MenuItem>
              <MenuItem value="Energy">Energy</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
<TextField
  fullWidth
  label="Annual Revenue"
  value={formatCurrency(context.parentCompany?.revenue)} // Added optional chaining
  onChange={(e) => handleContextChange('parentCompany', 'revenue', parseCurrency(e.target.value))}
  margin="normal"
  InputProps={{
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  }}
/>

        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Employees"
            type="number"
            value={context.parentCompany.employeeCount}
            onChange={(e) => handleContextChange('parentCompany', 'employeeCount', parseInt(e.target.value))}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} md={6}>
<TextField
  fullWidth
  label="Annual IT Budget"
  value={formatCurrency(context.parentCompany?.annualITBudget)} // Added optional chaining
  onChange={(e) => handleContextChange('parentCompany', 'annualITBudget', parseCurrency(e.target.value))}
  margin="normal"
  InputProps={{
    startAdornment: <InputAdornment position="start">$</InputAdornment>,
  }}
/>

        </Grid>
        <Grid item xs={12} md={6}>
<TextField
  fullWidth
  label="Operating Margin"
  value={context.parentCompany?.operatingMargin ? (context.parentCompany.operatingMargin * 100).toFixed(1) : '0'} // Added safety check
  onChange={(e) => handleContextChange('parentCompany', 'operatingMargin', parseFloat(e.target.value) / 100)}
  margin="normal"
  InputProps={{
    endAdornment: <InputAdornment position="end">%</InputAdornment>,
  }}
/>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Revenue Growth Rate"
            value={(context.parentCompany.revenueGrowthRate * 100).toFixed(1)}
            onChange={(e) => handleContextChange('parentCompany', 'revenueGrowthRate', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Digital Maturity</InputLabel>
            <Select
              value={context.parentCompany.digitalMaturity}
              label="Digital Maturity"
              onChange={(e) => handleContextChange('parentCompany', 'digitalMaturity', e.target.value)}
            >
              <MenuItem value="Very High">Very High</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Very Low">Very Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Primary Currency</InputLabel>
            <Select
              value={context.parentCompany.primaryCurrency}
              label="Primary Currency"
              onChange={(e) => handleContextChange('parentCompany', 'primaryCurrency', e.target.value)}
            >
              <MenuItem value="USD">USD ($)</MenuItem>
              <MenuItem value="EUR">EUR (€)</MenuItem>
              <MenuItem value="GBP">GBP (£)</MenuItem>
              <MenuItem value="INR">INR (₹)</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const GCCCompanySection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        🌍 GCC Company Information
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure your Global Capability Center (GCC) details and location-specific parameters.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="GCC Company Name"
            value={context.gccCompany.name}
            onChange={(e) => handleContextChange('gccCompany', 'name', e.target.value)}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>GCC Location</InputLabel>
            <Select
              value={context.gccCompany.location}
              label="GCC Location"
              onChange={(e) => handleContextChange('gccCompany', 'location', e.target.value)}
            >
              <MenuItem value="India">India</MenuItem>
              <MenuItem value="Poland">Poland</MenuItem>
              <MenuItem value="Mexico">Mexico</MenuItem>
              <MenuItem value="Brazil">Brazil</MenuItem>
              <MenuItem value="Philippines">Philippines</MenuItem>
              <MenuItem value="Malaysia">Malaysia</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Target GCC Size"
            type="number"
            value={context.gccCompany.targetSize}
            onChange={(e) => handleContextChange('gccCompany', 'targetSize', parseInt(e.target.value))}
            margin="normal"
            helperText="Number of employees in target GCC"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Current GCC Size"
            type="number"
            value={context.gccCompany.currentSize}
            onChange={(e) => handleContextChange('gccCompany', 'currentSize', parseInt(e.target.value))}
            margin="normal"
            helperText="Current number of employees (0 if planning)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>GCC Maturity Level</InputLabel>
            <Select
              value={context.gccCompany.maturityLevel}
              label="GCC Maturity Level"
              onChange={(e) => handleContextChange('gccCompany', 'maturityLevel', e.target.value)}
            >
              <MenuItem value="Planned">Planned</MenuItem>
              <MenuItem value="Establishing">Establishing (0-12 months)</MenuItem>
              <MenuItem value="Operational">Operational (1-3 years)</MenuItem>
              <MenuItem value="Mature">Mature (3+ years)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Implementation Timeline"
            type="number"
            value={context.gccCompany.implementationTimeline}
            onChange={(e) => handleContextChange('gccCompany', 'implementationTimeline', parseInt(e.target.value))}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">months</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Strategic Intent"
            value={context.gccCompany.strategicIntent}
            onChange={(e) => handleContextChange('gccCompany', 'strategicIntent', e.target.value)}
            margin="normal"
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Budget Allocation"
            value={context.gccCompany.budgetAllocation}
            onChange={(e) => handleContextChange('gccCompany', 'budgetAllocation', e.target.value)}
            margin="normal"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            GCC Location Advantages
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography gutterBottom>
              Talent Cost Advantage: {((1 - context.gccCompany.talentCostRatio) * 100).toFixed(0)}%
            </Typography>
            <Slider
              value={context.gccCompany.talentCostRatio}
              onChange={(e, newValue) => handleContextChange('gccCompany', 'talentCostRatio', newValue)}
              min={0.3}
              max={1.2}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${((1 - value) * 100).toFixed(0)}%`}
            />
            <Typography variant="caption" color="text.secondary">
              GCC talent cost as percentage of HQ cost
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography gutterBottom>
              Expected Efficiency Gain: {(context.gccCompany.expectedEfficiencyGain * 100).toFixed(0)}%
            </Typography>
            <Slider
              value={context.gccCompany.expectedEfficiencyGain}
              onChange={(e, newValue) => handleContextChange('gccCompany', 'expectedEfficiencyGain', newValue)}
              min={0.1}
              max={0.8}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography gutterBottom>
              Innovation Contribution: {(context.gccCompany.innovationContribution * 100).toFixed(0)}%
            </Typography>
            <Slider
              value={context.gccCompany.innovationContribution}
              onChange={(e, newValue) => handleContextChange('gccCompany', 'innovationContribution', newValue)}
              min={0.05}
              max={0.5}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography gutterBottom>
              Infrastructure Rating: {context.gccCompany.infrastructureRating}/5
            </Typography>
            <Slider
              value={context.gccCompany.infrastructureRating}
              onChange={(e, newValue) => handleContextChange('gccCompany', 'infrastructureRating', newValue)}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const FinancialParametersSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        💰 Financial Parameters
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure financial parameters for accurate ROI calculations and value assessment.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Discount Rate (WACC)"
            value={(context.financialParameters.discountRate * 100).toFixed(1)}
            onChange={(e) => handleContextChange('financialParameters', 'discountRate', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Weighted Average Cost of Capital"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tax Rate"
            value={(context.financialParameters.taxRate * 100).toFixed(1)}
            onChange={(e) => handleContextChange('financialParameters', 'taxRate', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Cost of Capital"
            value={(context.financialParameters.costOfCapital * 100).toFixed(1)}
            onChange={(e) => handleContextChange('financialParameters', 'costOfCapital', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Working Capital Ratio"
            value={(context.financialParameters.workingCapitalRatio * 100).toFixed(1)}
            onChange={(e) => handleContextChange('financialParameters', 'workingCapitalRatio', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Exchange Rate (Local to USD)"
            value={context.financialParameters.exchangeRate}
            onChange={(e) => handleContextChange('financialParameters', 'exchangeRate', parseFloat(e.target.value))}
            margin="normal"
            type="number"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Inflation Rate"
            value={(context.financialParameters.inflationRate * 100).toFixed(1)}
            onChange={(e) => handleContextChange('financialParameters', 'inflationRate', parseFloat(e.target.value) / 100)}
            margin="normal"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const sectionComponents = {
    parent: <ParentCompanySection />,
    gcc: <GCCCompanySection />,
    financial: <FinancialParametersSection />
  };

  const steps = [
    { label: 'Parent Company', section: 'parent' },
    { label: 'GCC Details', section: 'gcc' },
    { label: 'Financial Parameters', section: 'financial' }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CorporateFare color="primary" />
            <Typography variant="h5" component="h2">
              Company Context Configuration
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure parent company and GCC details for accurate value analysis
          {hasChanges && (
            <Chip 
              label="Unsaved Changes" 
              color="warning" 
              size="small" 
              sx={{ ml: 1 }} 
            />
          )}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper Navigation */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel 
                onClick={() => {
                  setActiveStep(index);
                  setActiveSection(step.section);
                }}
                sx={{ cursor: 'pointer' }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Active Section Content */}
        {sectionComponents[activeSection]}

        {/* Save Message */}
        {saveMessage && (
          <Alert 
            severity={saveMessage.includes('Error') ? 'error' : 'success'} 
            sx={{ mt: 2 }}
          >
            {saveMessage}
          </Alert>
        )}

        {/* Configuration Summary */}
<Card sx={{ mt: 3, bgcolor: 'primary.50' }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      Configuration Summary
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="body2">
          <strong>Parent:</strong> {context.parentCompany?.name || 'Not set'}
        </Typography>
        <Typography variant="body2">
          <strong>Revenue:</strong> {formatCurrency(context.parentCompany?.revenue)}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="body2">
          <strong>GCC:</strong> {context.gccCompany?.name || 'Not set'}
        </Typography>
        <Typography variant="body2">
          <strong>Location:</strong> {context.gccCompany?.location || 'Not set'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="body2">
          <strong>Target Size:</strong> {context.gccCompany?.targetSize || 0} employees
        </Typography>
        <Typography variant="body2">
          <strong>Cost Advantage:</strong> {context.gccCompany?.talentCostRatio ? ((1 - context.gccCompany.talentCostRatio) * 100).toFixed(0) : '0'}%
        </Typography>
      </Grid>
    </Grid>
  </CardContent>
</Card>

      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={() => setContext(JSON.parse(JSON.stringify(DEFAULT_COMPANY_CONTEXT)))}
          startIcon={<Close />}
          variant="outlined"
          color="secondary"
        >
          Reset All
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {activeStep > 0 && (
          <Button
            onClick={() => {
              setActiveStep(activeStep - 1);
              setActiveSection(steps[activeStep - 1].section);
            }}
            variant="outlined"
          >
            Back
          </Button>
        )}
        
        {activeStep < steps.length - 1 && (
          <Button
            onClick={() => {
              setActiveStep(activeStep + 1);
              setActiveSection(steps[activeStep + 1].section);
            }}
            variant="contained"
          >
            Next
          </Button>
        )}
        
        {activeStep === steps.length - 1 && (
          <Button
            onClick={handleSave}
            startIcon={<Save />}
            variant="contained"
            disabled={!hasChanges || isSubmitting || !user?.is_subscribed}
          >
            {isSubmitting ? 'Saving...' : 'Save to Database'}
          </Button>
        )}
      </DialogActions>

      {/* Subscription Warning */}
      {user && !user.is_subscribed && (
        <Alert severity="warning" sx={{ m: 2 }}>
          Subscription required to save company context. Please upgrade your plan.
        </Alert>
      )}
    </Dialog>
  );
};

export default CompanyContextConfiguration;
export { DEFAULT_COMPANY_CONTEXT };